/**
 * useInsolationWindows — extracts illuminated window openings from the mounted SVG.
 *
 * Returns an array of `{ id_hex, candidateEdges, sun_light_ranges, compartmentNumber }`
 * objects where `candidateEdges` are the two longest edges of the sill polygon
 * (the long sides of the sill rectangle), already transformed by the parent
 * group's `transform="translate(…) rotate(…)"`. The renderer picks which one
 * is the outer edge based on the compartment centroid.
 *
 * Window metadata is matched by `id_hex` against `product.tiers[].windows[]`.
 * If a window from the SVG has no metadata in the product data, it is skipped
 * (we don't know its `sun_light_ranges`).
 */

import { useMemo } from 'react'

import {
  getBoundaryPoints,
  parseTransform,
  transformPoint,
  getTopTwoLongEdges,
  normalizeIdHex,
  normalizeSunLightRanges,
} from '../utils/svgInsolation.js'

/**
 * @param {object} params
 * @param {SVGElement|null} params.svgElement
 * @param {object|null} params.product
 * @param {number|null} params.refTier
 * @returns {Array<{ id_hex: string, candidateEdges: [[number,number],[number,number]][], sun_light_ranges: [number,number][], compartmentNumber: number|null }>}
 */
export function useInsolationWindows({ svgElement, product, refTier }) {
  return useMemo(() => {
    if (!svgElement || !product) return []

    // Build id_hex → window metadata for the active tier (and across all tiers
    // as a fallback — generator usually scopes by tier but be permissive).
    const metaByHex = new Map()
    const tiers = Array.isArray(product?.tiers) ? product.tiers : []
    const targetTier = tiers.find((t) => Number(t?.ref_tier) === Number(refTier))
    const tierWindows = Array.isArray(targetTier?.windows) ? targetTier.windows : []
    for (const meta of tierWindows) {
      if (!meta?.id_hex) continue
      metaByHex.set(normalizeIdHex(meta.id_hex), meta)
    }
    // Fallback — fold in any other tier windows we haven't seen
    for (const tier of tiers) {
      if (tier === targetTier) continue
      for (const meta of (Array.isArray(tier?.windows) ? tier.windows : [])) {
        if (!meta?.id_hex) continue
        const key = normalizeIdHex(meta.id_hex)
        if (!metaByHex.has(key)) metaByHex.set(key, meta)
      }
    }

    const groups = svgElement.querySelectorAll('.openings_window g[id_hex], .openings_window .window')
    const out = []

    for (const group of groups) {
      const idHexAttr = group.getAttribute('id_hex')
      if (!idHexAttr) continue

      const idHex = normalizeIdHex(idHexAttr)
      const meta = metaByHex.get(idHex)
      if (!meta) continue // hard match — skip unknown windows

      const sill = group.querySelector('.opening_sill')
      if (!sill) continue
      const pathData = sill.getAttribute('d')
      if (!pathData) continue

      const boundary = getBoundaryPoints(pathData)
      if (boundary.length < 2) continue

      const transform = parseTransform(group.getAttribute('transform') ?? '')
      const transformed = boundary.map(([x, y]) => transformPoint(x, y, transform))

      const candidateEdges = getTopTwoLongEdges(transformed)
      if (candidateEdges.length === 0) continue

      // Window data uses `from_room` as the compartment number.
      const fromRoom = Number(meta.from_room)

      out.push({
        id_hex: idHex,
        candidateEdges,
        sun_light_ranges: normalizeSunLightRanges(meta.sun_light_ranges),
        compartmentNumber: Number.isFinite(fromRoom) ? fromRoom : null,
      })
    }

    return out
  }, [svgElement, product, refTier])
}
